import React, { useCallback, useMemo, useState } from 'react';
import { Checkbox, Divider, Flex, Select } from 'antd';
import type { SelectProps } from 'antd';
import { SearchInput, TeamTagPill, EllipsisTooltip } from '@/components';
import { CloseIcon, DoubleChevronDownRoleIcon } from '@/assets/icons/icons';
import styles from './TeamUserSelect.module.css';
import type { TeamGroup } from '@/apis/teamGroups';
import {
  groupMatchesSearch,
  groupSelectionState,
  teamPickerSectionLabels,
  toGroupValue,
  toggleGroupSelection,
} from '@/common/utils/teamGroups';

export interface TeamUser {
  id: string;
  name?: string;
  email?: string;
  teamId: string;
}

export interface TeamData {
  teamId: string;
  teamName: string;
  color?: string;
  users: TeamUser[];
}

/** The clean value exposed to consumers — no encoded strings. */
export interface AdditionalOption {
  id: string;
  name: string;
  disabled?: boolean;
  disabledReason?: string;
}

export const DEFAULT_ADDITIONAL_OPTIONS: AdditionalOption[] = [
  { id: 'ORIGIN_PRIMARY', name: 'Origin primary contact' },
  { id: 'DESTINATION_PRIMARY', name: 'Destination primary contact' },
];

const INLINE_BOTTOM_PLACEMENTS: NonNullable<SelectProps['builtinPlacements']> =
  {
    bottomLeft: {
      points: ['tl', 'bl'],
      offset: [0, 4],
      overflow: {
        adjustX: true,
        adjustY: false,
        shiftY: false,
      },
    },
  };

export interface TeamUserSelection {
  /** Teams selected as a whole ("all members of team X"). */
  teamIds: string[];
  /** Individual user IDs selected outside of a whole-team selection. */
  userIds: string[];
}

export interface TeamUserSelectProps {
  /** Available teams with their nested users. */
  teamsData: TeamData[];
  /** Standalone / additional options (e.g. Origin primary contact, Destination primary contact). */
  additionalOptions?: AdditionalOption[];
  /** Current selection state. */
  value: TeamUserSelection;
  /** Called when the selection changes. */
  onChange: (selection: TeamUserSelection) => void;
  /** Return true to grey-out / disable a specific user row. */
  isUserDisabled?: (user: TeamUser) => boolean;
  /** Return true to disable the "all members" toggle for a team. */
  isTeamSelectAllDisabled?: (teamId: string) => boolean;
  /** Disables the entire component. */
  disabled?: boolean;
  /** Placeholder text when nothing is selected. */
  placeholder?: string;
  /** Keeps the picker open in this component's layout. */
  openInline?: boolean;
  /**
   * Opt-in team groups, shown above the teams. Checking one selects every
   * team team as a whole team (like checking each team's header); unchecking
   * clears them. Groups themselves are never part of the value.
   */
  teamGroups?: TeamGroup[];
  /**
   * Replaces the double chevron on the field and on each team row (which opens its people), for a
   * screen whose design draws the product's single chevron. The row's turns over when it opens.
   */
  chevron?: React.ReactNode;
}

/**
 * Multi-select dropdown for choosing users, grouped by team.
 *
 * Each team is shown as a header row with a checkbox on the left ("all
 * members") and a chevron on the right (accordion expand/collapse). Individual
 * users appear beneath when the team is expanded. Includes a search input for
 * filtering by team/user name or email.
 *
 * Consumer-agnostic: notification-specific concerns (e.g. which notification
 * methods a user supports, "origin primary contact" entries) are left to the
 */
const TeamUserSelect: React.FC<TeamUserSelectProps> = ({
  teamsData,
  additionalOptions,
  value,
  onChange,
  isUserDisabled,
  isTeamSelectAllDisabled,
  disabled = false,
  placeholder = 'Select...',
  openInline = false,
  teamGroups,
  chevron,
}) => {
  const [searchValue, setSearchValue] = useState('');

  /**
   * Standalone / additional recipient options (e.g. Origin primary contact, Destination primary contact).
   */
  const effectiveAdditionalOptions = useMemo(
    () => additionalOptions ?? DEFAULT_ADDITIONAL_OPTIONS,
    [additionalOptions]
  );

  /** Filter standalone additional options by the search string. */
  const filteredAdditionalOptions = useMemo(() => {
    const q = searchValue.toLowerCase();
    if (!q) return effectiveAdditionalOptions;
    return effectiveAdditionalOptions.filter((opt) =>
      opt.name.toLowerCase().includes(q)
    );
  }, [effectiveAdditionalOptions, searchValue]);

  /** Toggle a standalone additional user option (e.g. Origin primary contact). */
  const toggleAdditionalUser = useCallback(
    (id: string) => {
      if (value.userIds.includes(id)) {
        onChange({
          teamIds: value.teamIds,
          userIds: value.userIds.filter((uid) => uid !== id),
        });
      } else {
        onChange({
          teamIds: value.teamIds,
          userIds: [...value.userIds, id],
        });
      }
    },
    [value, onChange]
  );

  /**
   * Tracks which team panels are expanded (accordion state).
   * This is completely independent from selection state.
   */
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(
    () => new Set()
  );

  /** Filter teams and users by the search string. If the team name matches,
   *  all its users are shown; otherwise only matching users are kept. */
  const filteredTeamsData = useMemo(() => {
    const q = searchValue.toLowerCase();
    if (!q) {
      return teamsData;
    }
    return teamsData
      .map((team) => {
        if (team.teamName.toLowerCase().includes(q)) {
          return team;
        }
        const matchedUsers = team.users.filter(
          (u) =>
            u.name?.toLowerCase().includes(q) ||
            u.email?.toLowerCase().includes(q)
        );
        if (matchedUsers.length > 0) {
          return { ...team, users: matchedUsers };
        }
        return null;
      })
      .filter((t): t is TeamData => t !== null);
  }, [teamsData, searchValue]);

  const isTeamSelected = useCallback(
    (teamId: string) => value.teamIds.includes(teamId),
    [value.teamIds]
  );

  const isUserSelected = useCallback(
    (userId: string) => value.userIds.includes(userId),
    [value.userIds]
  );

  /** A user is effectively checked if their team is fully selected OR they
   *  are individually selected. */
  const isChecked = useCallback(
    (user: TeamUser) => isTeamSelected(user.teamId) || isUserSelected(user.id),
    [isTeamSelected, isUserSelected]
  );

  /** Toggle an entire team on or off. When toggling on, any individually
   *  selected users from that team are removed (subsumed by the team). */
  const toggleTeam = useCallback(
    (teamId: string) => {
      if (isTeamSelected(teamId)) {
        // Untoggle: remove team from teamIds
        onChange({
          teamIds: value.teamIds.filter((id) => id !== teamId),
          userIds: value.userIds,
        });
      } else {
        // Toggle on: add to teamIds, remove individual users from this team
        const team = teamsData.find((t) => t.teamId === teamId);
        const teamUserIds = new Set((team?.users ?? []).map((u) => u.id));
        onChange({
          teamIds: [...value.teamIds, teamId],
          userIds: value.userIds.filter((uid) => !teamUserIds.has(uid)),
        });
      }
    },
    [value, onChange, teamsData, isTeamSelected]
  );

  /** Groups limited to teams that can be picked as a whole team here. */
  const pickableGroups = useMemo(() => {
    if (!teamGroups?.length) return [];
    const pickable = new Set(
      teamsData
        .filter(
          (team) =>
            team.users.length > 0 && !isTeamSelectAllDisabled?.(team.teamId)
        )
        .map((team) => team.teamId)
    );
    return teamGroups
      .map((group) => ({
        ...group,
        team_ids: group.team_ids.filter((id) => pickable.has(id)),
      }))
      .filter(
        (group) =>
          group.team_ids.length > 0 &&
          groupMatchesSearch(
            group,
            searchValue,
            (id) => teamsData.find((team) => team.teamId === id)?.teamName
          )
      );
  }, [teamGroups, teamsData, isTeamSelectAllDisabled, searchValue]);

  /** Toggle a group: select every team as a whole team, or clear them all.
   *  Individually selected users of newly selected teams are subsumed. */
  const toggleGroup = useCallback(
    (group: TeamGroup) => {
      const nextTeamIds = toggleGroupSelection(group, value.teamIds);
      const added = new Set(
        nextTeamIds.filter((id) => !value.teamIds.includes(id))
      );
      const subsumedUserIds = new Set(
        teamsData
          .filter((team) => added.has(team.teamId))
          .flatMap((team) => team.users.map((u) => u.id))
      );
      onChange({
        teamIds: nextTeamIds,
        userIds: value.userIds.filter((uid) => !subsumedUserIds.has(uid)),
      });
    },
    [value, onChange, teamsData]
  );

  /** Toggle a single user. If the team was fully selected, unchecking one
   *  user demotes it: the team is removed and the remaining users become
   *  individual selections. If checking this user completes the set of all
   *  enabled users in the team, auto-promotes to a team selection. */
  const toggleUser = useCallback(
    (user: TeamUser) => {
      const { teamId, id: userId } = user;

      if (isTeamSelected(teamId)) {
        // Team is fully selected — unchecking one user demotes:
        // remove team, add all OTHER enabled users as individual
        const team = teamsData.find((t) => t.teamId === teamId);
        const remainingUserIds = (team?.users ?? [])
          .filter((u) => u.id !== userId && !isUserDisabled?.(u))
          .map((u) => u.id);
        onChange({
          teamIds: value.teamIds.filter((id) => id !== teamId),
          userIds: [...value.userIds, ...remainingUserIds],
        });
      } else if (isUserSelected(userId)) {
        // Uncheck individual user
        onChange({
          teamIds: value.teamIds,
          userIds: value.userIds.filter((id) => id !== userId),
        });
      } else {
        // Check individual user — auto-promote to team if all enabled users
        // in the team are now selected
        const team = teamsData.find((t) => t.teamId === teamId);
        const enabledUsers = (team?.users ?? []).filter(
          (u) => !isUserDisabled?.(u)
        );
        const newUserIds = [...value.userIds, userId];
        const allEnabled = enabledUsers.every(
          (u) => u.id === userId || newUserIds.includes(u.id)
        );

        if (allEnabled && enabledUsers.length > 0) {
          const enabledSet = new Set(enabledUsers.map((u) => u.id));
          onChange({
            teamIds: [...value.teamIds, teamId],
            userIds: newUserIds.filter((uid) => !enabledSet.has(uid)),
          });
        } else {
          onChange({ teamIds: value.teamIds, userIds: newUserIds });
        }
      }
    },
    [value, onChange, teamsData, isTeamSelected, isUserSelected, isUserDisabled]
  );

  /** Ant Design's multi-Select requires a flat string[] as its `value` prop.
   *  We encode our structured TeamUserSelection into strings like
   *  `"teamId::ALL_MEMBERS"` (whole team) or `"teamId::userId"` (individual).
   *  These are only used internally — consumers never see them. */
  const encodedValue = useMemo(() => {
    const tokens: string[] = [];
    for (const teamId of value.teamIds) {
      tokens.push(`${teamId}::ALL_MEMBERS`);
    }
    for (const userId of value.userIds) {
      const isAdditional = effectiveAdditionalOptions.some(
        (opt) => opt.id === userId
      );
      if (isAdditional) {
        tokens.push(`additional::${userId}`);
      } else {
        const team = teamsData.find((t) =>
          t.users.some((u) => u.id === userId)
        );
        if (team) {
          tokens.push(`${team.teamId}::${userId}`);
        } else {
          tokens.push(`additional::${userId}`);
        }
      }
    }
    return tokens;
  }, [value, teamsData, effectiveAdditionalOptions]);

  /** Map from encoded token → display label for tag rendering. */
  const labelMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (const opt of effectiveAdditionalOptions) {
      map[`additional::${opt.id}`] = opt.name;
      map[opt.id] = opt.name;
    }
    for (const team of teamsData) {
      map[`${team.teamId}::ALL_MEMBERS`] = `All members of ${team.teamName}`;
      for (const user of team.users) {
        map[`${team.teamId}::${user.id}`] = user.name || user.email || user.id;
      }
    }
    return map;
  }, [teamsData, effectiveAdditionalOptions]);

  /** Remove a token (team or user) when its tag is closed. */
  const removeToken = useCallback(
    (token: string) => {
      if (token.startsWith('additional::')) {
        const rest = token.slice('additional::'.length);
        onChange({
          teamIds: value.teamIds,
          userIds: value.userIds.filter((id) => id !== rest),
        });
        return;
      }

      const sep = token.indexOf('::');
      const teamId = token.slice(0, sep);
      const rest = token.slice(sep + 2);

      if (rest === 'ALL_MEMBERS') {
        onChange({
          teamIds: value.teamIds.filter((id) => id !== teamId),
          userIds: value.userIds,
        });
      } else if (isTeamSelected(teamId)) {
        // User tag under a fully-selected team — demote.
        // Only include enabled users in the demotion, consistent with auto-promote.
        const team = teamsData.find((t) => t.teamId === teamId);
        const remainingUserIds = (team?.users ?? [])
          .filter((u) => u.id !== rest && !isUserDisabled?.(u))
          .map((u) => u.id);
        onChange({
          teamIds: value.teamIds.filter((id) => id !== teamId),
          userIds: [...value.userIds, ...remainingUserIds],
        });
      } else {
        onChange({
          teamIds: value.teamIds,
          userIds: value.userIds.filter((id) => id !== rest),
        });
      }
    },
    [value, onChange, teamsData, isTeamSelected, isUserDisabled]
  );

  /** Toggle accordion expansion for a team (independent of selection). */
  const toggleExpanded = useCallback((teamId: string) => {
    setExpandedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) {
        next.delete(teamId);
      } else {
        next.add(teamId);
      }
      return next;
    });
  }, []);

  /** Clear all selections (both whole teams and individual users). */
  const handleClearAll = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      onChange({ teamIds: [], userIds: [] });
    },
    [onChange]
  );

  const teamOptions = filteredTeamsData.map((team) => {
    const hasUsers = team.users.length > 0;
    const checkboxDisabled =
      disabled || isTeamSelectAllDisabled?.(team.teamId) || !hasUsers;
    const isExpanded = expandedTeams.has(team.teamId);
    const headerStyle = checkboxDisabled
      ? styles.teamHeaderDisabled
      : styles.teamHeaderEnabled;

    return (
      <React.Fragment key={team.teamId}>
        {/* Team header row: [Checkbox] [Dot] [Name] ... [Chevron | No users] */}
        <Select.Option
          key={`team::${team.teamId}`}
          value={`team::${team.teamId}`}
          className={`${styles.teamHeader} ${headerStyle}`}
        >
          <Flex justify='space-between' align='center'>
            {/* Left: checkbox + color dot + team name */}
            <Flex
              align='center'
              gap={8}
              // Prevent the disabled Option from swallowing the checkbox click
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                if (!checkboxDisabled) {
                  e.stopPropagation();
                  toggleTeam(team.teamId);
                }
              }}
              className={`${styles.teamCheckboxArea} ${checkboxDisabled ? styles.teamCheckboxAreaDisabled : ''}`}
            >
              <Checkbox
                data-testid={`team-checkbox-${team.teamId}`}
                checked={isTeamSelected(team.teamId)}
                disabled={!!checkboxDisabled}
                className='checkbox-md'
              />
              <span
                className={styles.teamColorDot}
                // eslint-disable-next-line react/forbid-dom-props -- each team's own color, from data
                style={{
                  backgroundColor: team.color || 'var(--text-primary)',
                }}
              />
              <EllipsisTooltip
                text={team.teamName}
                className={
                  checkboxDisabled
                    ? styles.teamNameDisabled
                    : styles.teamNameEnabled
                }
              />
            </Flex>

            {/* Right: chevron (expand/collapse) or "No users" label */}
            {hasUsers ? (
              <button
                data-testid={`team-chevron-${team.teamId}`}
                type='button'
                className={styles.chevronBtn}
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleExpanded(team.teamId);
                }}
                aria-label={
                  isExpanded
                    ? `Collapse ${team.teamName}`
                    : `Expand ${team.teamName}`
                }
                aria-expanded={isExpanded}
              >
                {chevron ? (
                  <span
                    className={`${styles.givenChevron} ${
                      isExpanded ? styles.givenChevronOpen : ''
                    }`}
                  >
                    {chevron}
                  </span>
                ) : (
                  <DoubleChevronDownRoleIcon
                    fill='var(--text-secondary)'
                    style={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                      display: 'block',
                    }}
                  />
                )}
              </button>
            ) : (
              <span className={styles.noUsersText}>No users</span>
            )}
          </Flex>
        </Select.Option>

        {/* Individual user rows — only rendered when team is expanded */}
        {hasUsers &&
          isExpanded &&
          team.users.map((user) => {
            const userDisabled = disabled || (isUserDisabled?.(user) ?? false);
            const displayName = user.name || user.email || user.id;

            return (
              <Select.Option
                key={`${team.teamId}::${user.id}`}
                value={`${team.teamId}::${user.id}`}
                label={displayName}
                disabled={userDisabled}
                className={styles.userOption}
                style={{ padding: 0 }}
              >
                <div
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    if (!userDisabled) {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleUser(user);
                    }
                  }}
                  className={`${styles.userRow} ${
                    userDisabled
                      ? styles.userRowDisabled
                      : styles.userRowEnabled
                  }`}
                >
                  <Checkbox
                    checked={isChecked(user)}
                    disabled={userDisabled}
                    style={{ marginRight: 8 }}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => toggleUser(user)}
                    className='checkbox-md'
                  />
                  <EllipsisTooltip
                    text={displayName}
                    className={styles.userOptionText}
                  />
                </div>
              </Select.Option>
            );
          })}
      </React.Fragment>
    );
  });

  const select = (
    <Select
      mode='multiple'
      placeholder={placeholder}
      disabled={disabled}
      className={styles.select}
      value={encodedValue}
      suffixIcon={
        <Flex align='center' gap={8}>
          {!disabled &&
            (value.teamIds.length > 0 || value.userIds.length > 0) && (
              <button
                type='button'
                className='teams-dropdown-lb-clear-btn'
                onClick={handleClearAll}
                title='Clear all'
              >
                <CloseIcon />
              </button>
            )}
          {chevron ?? <DoubleChevronDownRoleIcon width={16} height={16} />}
        </Flex>
      }
      // Disable Ant Design's built-in search — we render our own SearchInput
      // inside popupRender to avoid double-filtering.
      showSearch={false}
      filterOption={false}
      optionLabelProp='label'
      open={openInline ? true : undefined}
      placement={openInline ? 'bottomLeft' : undefined}
      builtinPlacements={openInline ? INLINE_BOTTOM_PLACEMENTS : undefined}
      getPopupContainer={
        openInline
          ? (triggerNode) => triggerNode.parentElement ?? document.body
          : undefined
      }
      classNames={
        openInline ? { popup: { root: styles.inlinePopup } } : undefined
      }
      popupRender={(menu) => (
        <div className={styles.popupScroll}>
          <div className={styles.popupSearch}>
            <SearchInput
              placeholder='Search recipients...'
              onChange={(v: string) => setSearchValue(v?.toLowerCase() ?? '')}
              width='100%'
              onClear={() => setSearchValue('')}
            />
          </div>
          <Divider style={{ margin: '10px 0 8px 0' }} />
          {menu}
        </div>
      )}
      tagRender={({ value: val, onClose }) => {
        const token = String(val ?? '');
        if (!token) {
          return <></>;
        }
        const label = labelMap[token] || token;
        // Resolve the team color for the dot — extract teamId from the token
        const teamId = token.slice(0, token.indexOf('::'));
        const teamColor = teamsData.find((t) => t.teamId === teamId)?.color;
        return (
          // Prevent mouse-down on the pill from propagating to the Select,
          // which would toggle the dropdown open/close or steal focus.
          <span
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            className={styles.tagWrapper}
          >
            <TeamTagPill
              name={label}
              color={teamColor}
              removable={!disabled}
              onRemove={(e) => {
                e.preventDefault();
                removeToken(token);
                // The pill's remove button is an HTML element, which onClose expects.
                onClose?.(e as React.MouseEvent<HTMLElement, MouseEvent>);
              }}
            />
          </span>
        );
      }}
    >
      {/* Contacts first, as before team groups existed, so they never read
          as part of the group section. */}
      {filteredAdditionalOptions.map((opt) => {
        const optDisabled = disabled || (opt.disabled ?? false);
        const isOptionChecked = value.userIds.includes(opt.id);

        return (
          <Select.Option
            key={`additional::${opt.id}`}
            value={`additional::${opt.id}`}
            label={opt.name}
            disabled={optDisabled}
            className={styles.userOption}
            style={{ padding: 0 }}
          >
            <div
              title={optDisabled ? opt.disabledReason : undefined}
              onMouseDown={(e) => e.preventDefault()}
              onClick={(e) => {
                if (!optDisabled) {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleAdditionalUser(opt.id);
                }
              }}
              className={`${styles.userRow} ${
                optDisabled ? styles.userRowDisabled : styles.userRowEnabled
              }`}
            >
              <Checkbox
                checked={isOptionChecked}
                disabled={optDisabled}
                style={{ marginRight: 8 }}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleAdditionalUser(opt.id)}
                className='checkbox-md'
              />
              <EllipsisTooltip
                text={opt.name}
                className={styles.userOptionText}
              />
            </div>
          </Select.Option>
        );
      })}

      {pickableGroups.length > 0 && (
        <Select.OptGroup
          key='team-groups'
          label={teamPickerSectionLabels().groups}
        >
          {pickableGroups.map((group) => {
            const state = groupSelectionState(group, value.teamIds);
            return (
              <Select.Option
                key={toGroupValue(group.id)}
                value={toGroupValue(group.id)}
                label={group.name}
                disabled={disabled}
                className={`${styles.teamHeader} ${
                  disabled
                    ? styles.teamHeaderDisabled
                    : styles.teamHeaderEnabled
                }`}
              >
                <Flex
                  align='center'
                  gap={8}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    if (!disabled) {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleGroup(group);
                    }
                  }}
                  className={styles.teamCheckboxArea}
                >
                  <Checkbox
                    data-testid={`team-group-checkbox-${group.id}`}
                    checked={state.checked}
                    indeterminate={state.indeterminate}
                    disabled={disabled}
                    className='checkbox-md'
                  />
                  <span
                    className={styles.teamColorDot}
                    // eslint-disable-next-line react/forbid-dom-props -- each team's own color, from data
                    style={{
                      backgroundColor: group.color || 'var(--text-primary)',
                    }}
                  />
                  <EllipsisTooltip
                    text={`${group.name} (${group.team_ids.length})`}
                    className={styles.teamNameEnabled}
                  />
                </Flex>
              </Select.Option>
            );
          })}
        </Select.OptGroup>
      )}

      {pickableGroups.length > 0 ? (
        <Select.OptGroup key='teams' label={teamPickerSectionLabels().teams}>
          {teamOptions}
        </Select.OptGroup>
      ) : (
        teamOptions
      )}
    </Select>
  );

  return openInline ? (
    <div className={styles.inlineContainer}>{select}</div>
  ) : (
    select
  );
};

export default TeamUserSelect;
